// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck

import { TextButton, TextLink, Text } from '@oneaudi/unified-web-components';
import styled from 'styled-components';
import { responsiveStyles } from '../../utils/responsiveStyles';
import type {
  HeaderStyledLoginProps,
  HeaderStyledShaderProps,
  HeaderStyledWrapperProps,
} from '../../interfaces/header-styled-components.interfaces';
import { timeL, timeXXL, timeS } from './AudiHeaderStyleUtils';
import { createBreakpointStyles } from '../../services';
import type { CustomTheme } from '../../../@types/theme';

const headerBreakpointStyles = (): Array<{
  maxNavItems: number;
  style: unknown;
}> => [
  {
    maxNavItems: 0,
    style: null,
  },
  {
    maxNavItems: 4,
    style: {
      'flex-wrap': {
        l: 'nowrap',
      },
      'max-width': {
        l: '100%',
      },
      overflow: {
        l: 'initial',
      },
      'z-index': {
        l: '1',
      },
    },
  },
  {
    maxNavItems: 5,
    style: {
      'flex-wrap': {
        xl: 'nowrap',
      },
      'max-width': {
        xl: '100%',
      },
      overflow: {
        xl: 'initial',
      },
      'z-index': {
        xl: '1',
      },
    },
  },
  {
    maxNavItems: 8,
    style: {
      'flex-wrap': {
        xl: 'nowrap',
      },
      'max-width': {
        xl: '100%',
      },
      overflow: {
        xl: 'initial',
      },
      'z-index': {
        xl: '1',
      },
    },
  },
  {
    maxNavItems: -1,
    style: null,
  },
];

export const HeaderStyled = styled.div<{
  disableTransition?: boolean;
}>`
  --one-header-color-canvas-default: ${({ theme }): string => theme.colors.colorCanvasDefault};
  --one-header-color-black: ${({ theme }): string => theme.colors.colorBaseBlack};
  --one-header-color-white: ${({ theme }): string => theme.colors.colorBaseWhite};
  --one-header-color-grey: ${({ theme }): string => theme.colors.colorBaseGrey5};
  --one-header-color-grey-light: ${({ theme }): string => theme.colors.colorGreyLight};
  --one-header-color-link: ${({ theme }): string => theme.colors.colorBaseGrey60};
  --one-header-color-hover: ${({ theme }): string => theme.colors.colorHover};
  --one-header-color-red-rhombus: ${({ theme }): string => theme.colors.colorProgressiveRed};
  --one-header-color-transparent: 'transparent';
  --one-header-height: 72px;
  --audi-rings-logo-height: 24px;
  --audi-rings-logo-width: 69px;
  --one-header-space-xs: ${({ theme }): string => theme.spacing.xs};
  --one-header-space-s: ${({ theme }): string => theme.spacing.s};
  --one-header-space-m: ${({ theme }): string => theme.spacing.m};
  --one-header-space-l: ${({ theme }): string => theme.spacing.l};
  --one-header-space-xl: ${({ theme }): string => theme.spacing.xl};
  --one-header-space-xxl: ${({ theme }): string => theme.spacing.xxl};
  --one-header-space-xxxl: ${({ theme }): string => theme.spacing.xxxl};
  --one-header-side-spacing: 16px;
  --one-header-alpha-menu-top-spacing: 26px;

  /* stylelint-disable */
  --one-header-flyout-left-spacing: calc(
    var(--one-header-side-spacing) + var(--audi-rings-logo-width) + var(--one-header-space-xl)
  );
  /* stylelint-enable */
  box-sizing: border-box;
  display: block;
  font-size: 10px;
  ${({ theme }): string =>
    responsiveStyles(
      {
        '--one-header-side-spacing': {
          l: '60px',
          m: '40px',
          s: '28px',
          xl: '96px',
          xxl: 'calc((100% - 1728px) / 2)',
        },
      },
      theme.breakpoints,
    )}
  position: relative;
  touch-action: manipulation;
  width: 100%;
  z-index: 100;

  & *,
  & *::after,
  & *::before {
    box-sizing: border-box;
    ${({ disableTransition }): string => disableTransition && 'transition: none !important;'}
  }
`;

export const HeaderStyledWrapper = styled.div<HeaderStyledWrapperProps>`
  background-color: ${({ theme }): string => theme.colors.colorCanvasDefault};
  display: flex;
  height: var(--one-header-height);
  ${({ headerNavigationItemsAmount, theme }): string =>
    responsiveStyles(
      createBreakpointStyles(headerNavigationItemsAmount, headerBreakpointStyles()),
      theme.breakpoints,
    )}
  padding: 0 var(--one-header-side-spacing);
  position: relative;
  z-index: 100;
`;

export const HeaderStyledShader = styled.div<HeaderStyledShaderProps>`
  transition: background-color ${timeS} ${({ theme }): string => theme.easing.easingDefault};
  background-color: var(
    ${({ showShader }): string =>
      showShader ? '--one-header-color-black' : '--one-header-color-transparent'}
  );
  bottom: 0;
  display: block;
  left: 0;
  opacity: 0.65;
  position: fixed;
  right: 0;
  top: 0;
  z-index: -1;
`;

export const HeaderStyledUserActionWrapper = styled.div<{
  isLoading: boolean;
}>`
  background-color: ${({ theme }): string =>
    theme.type === 'legacy' ? 'var(--one-header-color-white)' : ''};
  display: flex;
  flex-shrink: 0;
  justify-content: flex-end;
  opacity: ${({ isLoading }): string => (isLoading ? '0' : '1')};
  transition: opacity ${({ theme }): string => theme.easing.easingDefault} ${timeL} ${timeXXL};
  width: ${({ isLoading }): string => (isLoading ? '0px' : '33%')};
  z-index: 2;

  @media (min-width: 1440px) {
    flex-shrink: unset;
  }
`;

export const headerStyledLoginStyles = (): Array<{
  maxNavItems: number;
  style: unknown;
}> => [
  {
    maxNavItems: 0,
    style: null,
  },
  {
    maxNavItems: 4,
    style: {
      display: {
        l: 'block',
      },
    },
  },
  {
    maxNavItems: 5,
    style: {
      display: {
        xl: 'block',
      },
    },
  },
  {
    maxNavItems: 8,
    style: {
      display: {
        xxl: 'block',
      },
    },
  },
];

export const HeaderStyledLogin = styled.div<HeaderStyledLoginProps>`
  display: ${({ isOpen }): string => (isOpen ? 'none' : 'block')};
  margin: var(--one-header-space-s) 0;
  ${({ headerNavigationItemsAmount, theme }): string =>
    responsiveStyles(
      createBreakpointStyles(headerNavigationItemsAmount, headerStyledLoginStyles()),
      theme.breakpoints,
    )}
`;

export const StyledPopoverContainer = styled.div`
  display: flex;
  align-items: stretch;
  flex-direction: column;
  gap: 14;
  @media (min-width: 768px) {
    width: 328px;
    overflow-x: hidden;
  }
  svg {
    left: 327px;
  }

  .login-button {
    margin-bottom: ${({ theme }): string => theme.spacing.s};
  }
`;

export const StylesDetailsLink = styled(TextLink)`
  align-items: flex-start;
  text-decoration: none;
  padding: ${({ theme }): string => theme.spacing.m} 0;
  margin: 0 ${({ theme }): string => theme.spacing.xs} ${({ theme }): string => theme.spacing.xs}
    ${({ theme }): string => theme.spacing.m};

  span {
    gap: 0;
    &:hover {
      svg {
        transition: transform ${timeL} ${({ theme }): string => theme.easing.easingDefault};
        transform: translateX(5px);
      }
    }
  }
`;

export const StyledTextSupplierName = styled(Text)`
  color: ${({ theme }): string =>
    theme.type === 'legacy' ? theme.colors.colorBaseBlack : theme.colors.colorBaseGrey60};
  font-weight: 500;
  margin: 0 ${({ theme }): string => theme.spacing.s} 0 0;
`;
